import ErrorBoundary from "shared/components/error_boundary"

import type { UIEvent, UIResponse, UpgradeRequest } from "../types"
import ActionsDelayCard from "./actions_delay_card"
import AppletPreviewCard from "./applet_preview_card"
import AppletSuccessCard from "./applet_success_card"
import { CardFallback } from "./card_shell"
import CheckboxMultiCard from "./checkbox_multi_card"
import DatetimePickerCard from "./datetime_picker_card"
import LocationPickerCard from "./location_picker_card"
import SelectorCard from "./selector_card"
import ServiceConnectionCard from "./service_connection_card"
import TextInputCard from "./text_input_card"
import TimePickerCard from "./time_picker_card"
import UpgradeRequiredCard from "./upgrade_required_card"

interface Props {
  ui: UIEvent
  username?: string
  // Present once the card has been answered — the card renders disabled, recording the choice.
  response?: UIResponse
  onRespond: (
    uiId: string,
    actionId: string,
    value: unknown,
    displayText?: string,
    ingredients?: Record<string, string>
  ) => void
  onUpgradeRequest?: (request: UpgradeRequest) => void
}

function renderCard(
  ui: UIEvent,
  username: string,
  onRespond: Props["onRespond"],
  onUpgradeRequest: Props["onUpgradeRequest"],
  response: Props["response"]
) {
  switch (ui.type) {
    case "service_selector":
    case "trigger_selector":
    case "query_selector":
    case "action_selector":
    case "account_selector":
    case "collection_field_selector":
    case "double_collection_field_selector":
      return <SelectorCard ui={ui} onRespond={onRespond} />
    case "service_connection":
      return <ServiceConnectionCard ui={ui} onRespond={onRespond} response={response} />
    case "location_picker":
      return <LocationPickerCard ui={ui} onRespond={onRespond} />
    case "time_picker":
      return <TimePickerCard ui={ui} onRespond={onRespond} />
    case "datetime_picker":
      return <DatetimePickerCard ui={ui} onRespond={onRespond} />
    case "applet_success_card":
      return <AppletSuccessCard ui={ui} username={username} />
    case "checkbox_multi_field_selector":
      return <CheckboxMultiCard ui={ui} onRespond={onRespond} />
    case "applet_preview_card":
      return <AppletPreviewCard ui={ui} onRespond={onRespond} response={response} />
    case "text_input":
      return <TextInputCard ui={ui} onRespond={onRespond} />
    case "actions_delay_selector":
      return <ActionsDelayCard ui={ui} onRespond={onRespond} />
    case "upgrade_required":
      return (
        <UpgradeRequiredCard ui={ui} onRespond={onRespond} onUpgradeRequest={onUpgradeRequest} response={response} />
      )
    default:
      return <CardFallback text={ui.fallback_text || `(${ui.type})`} />
  }
}

const UIEventSlot = ({ ui, username, response, onRespond, onUpgradeRequest }: Props) => (
  <ErrorBoundary placeholder={<CardFallback text={ui.fallback_text || `(${ui.type})`} />}>
    {renderCard(ui, username ?? "", onRespond, onUpgradeRequest, response)}
  </ErrorBoundary>
)

export default UIEventSlot
